import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useSelector } from 'react-redux';
import { userSelector } from '../user/userSlice';

import api from '../../services/api';
import apiErrorFormatter from '../../services/apiErrorFormatter';

import DialogStartMaintenanceTask from './DialogStartMaintenanceTask';
import DialogFinishMaintenanceTask from './DialogFinishMaintenanceTask';
import DialogAcceptMaintenanceTask from './DialogAcceptMaintenanceTask';
import DialogRejectMaintenanceTask from './DialogRejectMaintenanceTask';
import DialogTakeOverMaintenanceTask from './DialogTakeOverMaintenanceTask';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

function DetailRow(props) {
  const { header, data } = props;

  return (
    <Box
      sx={{
        mb: '2px',
        px: 1,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: '24%',
          textAlign: { xs: 'center', sm: 'left' },
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {header}
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: '76%',
          textAlign: { xs: 'center', sm: 'left' },
        }}
      >
        <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
          {data}
        </Typography>
      </Box>
    </Box>
  );
}

function MaintenanceTaskDetails() {
  const { t } = useTranslation('maintenance');

  const { user } = useSelector(userSelector);

  const navigate = useNavigate();
  const location = useLocation();
  const maintenanceTaskId = location?.state?.maintenanceTaskId
    ? location?.state?.maintenanceTaskId
    : 0;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showContent, setShowContent] = useState(false);

  const [maintenanceTask, setMaintenanceTask] = useState({});

  const [
    isOpenDialogStartMaintenanceTask,
    setIsOpenDialogStartMaintenanceTask,
  ] = useState(false);
  const [
    isOpenDialogFinishMaintenanceTask,
    setIsOpenDialogFinishMaintenanceTask,
  ] = useState(false);
  const [
    isOpenDialogAcceptMaintenanceTask,
    setIsOpenDialogAcceptMaintenanceTask,
  ] = useState(false);
  const [
    isOpenDialogRejectMaintenanceTask,
    setIsOpenDialogRejectMaintenanceTask,
  ] = useState(false);
  const [
    isOpenDialogTakeOverMaintenanceTask,
    setIsOpenDialogTakeOverMaintenanceTask,
  ] = useState(false);

  useEffect(() => {
    maintenanceTaskId
      ? getData()
      : navigate('..', { relative: 'path', replace: true });
  }, [maintenanceTaskId]);

  const getData = async () => {
    setLoading(true);
    setErrors([]);
    setShowContent(false);
    setMaintenanceTask({});
    await api.get('/maintenance/maintenance-tasks/' + maintenanceTaskId).then(
      (res) => {
        setMaintenanceTask(res?.data);
        setShowContent(true);
      },
      (err) => {
        setErrors(apiErrorFormatter(err));
      }
    );
    setLoading(false);
  };

  return (
    <Container sx={{ mt: 1, mb: 2 }} maxWidth="false">
      <Box mb={1}>
        <Divider>{t('maintenance_task_details')}</Divider>
      </Box>
      <Paper sx={{ p: 1, textAlign: 'center' }} variant="outlined">
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            sx={{ px: 4, py: 2 }}
            disabled={loading}
            startIcon={
              !loading ? (
                <ArrowBackIcon />
              ) : (
                <CircularProgress color="inherit" size={20} />
              )
            }
            component={RouterLink}
            to=".."
            relative="path"
          >
            {t('common:back')}
          </Button>
          <Button
            variant="contained"
            sx={{ px: 4, py: 2 }}
            disabled={loading}
            startIcon={
              !loading ? (
                <RefreshIcon />
              ) : (
                <CircularProgress color="inherit" size={20} />
              )
            }
            onClick={getData}
          >
            {t('common:refresh')}
          </Button>
        </Box>
      </Paper>
      {!showContent && loading ? (
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <CircularProgress size={'60px'} />
        </Box>
      ) : null}
      {!showContent && errors.length > 0 ? (
        <Box mt={5}>
          {errors
            .slice()
            .reverse()
            .map((error) => (
              <Box key={error} mt={1}>
                <Alert variant="outlined" severity="error">
                  {t(error)}
                </Alert>
              </Box>
            ))}
        </Box>
      ) : null}
      {showContent ? (
        <Paper
          sx={{
            mt: 1,
            p: 1,
            textAlign: 'center',
            justifyContent: 'space-around',
          }}
          variant="outlined"
        >
          <Box>
            <Box
              sx={(theme) => ({
                mb: '4px',
                px: 1,
                py: '2px',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: 'info.main',
                // backgroundColor:
                //   theme.palette.mode === 'light' ? 'info.light' : 'info.dark',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                // justifyContent: { xs: 'space-between', lg: 'left' },
              })}
            >
              {/* <Box sx={{ flexBasis: '24%', textAlign: 'left' }}>
                {t('created')}
              </Box> */}
              {t('created')}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                  // textAlign: { xs: 'right', lg: 'left' },
                }}
              >
                <Typography variant="caption">
                  {maintenanceTask?.createdByUser?.username
                    ? maintenanceTask?.createdByUser?.username
                    : '-'}
                </Typography>
                <Typography variant="caption">
                  {maintenanceTask?.createdAt
                    ? moment(maintenanceTask?.createdAt).format('L LTS')
                    : '-'}
                </Typography>
              </Box>
            </Box>
            <DetailRow
              header={t('area')}
              data={maintenanceTask?.area?.areaName}
            />
            <DetailRow
              header={t('line')}
              data={maintenanceTask?.line?.lineName}
            />
            <DetailRow
              header={t('equipment')}
              data={maintenanceTask?.equipment?.equipmentName}
            />
            <DetailRow
              header={t('error_type')}
              data={t(maintenanceTask?.errorType?.errorTypeName)}
            />
            <DetailRow
              header={t('priority')}
              data={t(maintenanceTask?.priority?.priorityName)}
            />
            <DetailRow
              header={t('maintenance_type')}
              data={t(maintenanceTask?.maintenanceType?.maintenanceTypeName)}
            />
            <DetailRow
              header={t('common:comment')}
              data={
                maintenanceTask?.createdComment
                  ? maintenanceTask?.createdComment
                  : '-'
              }
            />
          </Box>
          <Box>
            <Box
              sx={(theme) => ({
                mt: 1,
                mb: '4px',
                px: 1,
                py: '2px',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: 'info.main',
                // backgroundColor:
                //   theme.palette.mode === 'light'
                //     ? 'primary.light'
                //     : 'primary.dark',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              })}
            >
              {t('started')}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }}
              >
                <Typography variant="caption">
                  {maintenanceTask?.startedByUser?.username
                    ? maintenanceTask?.startedByUser?.username
                    : '-'}
                </Typography>
                <Typography variant="caption">
                  {maintenanceTask?.startedAt
                    ? moment(maintenanceTask?.startedAt).format('L LTS')
                    : '-'}
                </Typography>
              </Box>
            </Box>
            <DetailRow
              header={t('common:comment')}
              data={
                maintenanceTask?.startedComment
                  ? maintenanceTask?.startedComment
                  : '-'
              }
            />
          </Box>
          <Box>
            <Box
              sx={(theme) => ({
                mt: 1,
                mb: '4px',
                px: 1,
                py: '2px',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: 'info.main',
                // backgroundColor:
                //   theme.palette.mode === 'light'
                //     ? 'primary.light'
                //     : 'primary.dark',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              })}
            >
              {t('finished')}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }}
              >
                <Typography variant="caption">
                  {maintenanceTask?.finishedByUser?.username
                    ? maintenanceTask?.finishedByUser?.username
                    : '-'}
                </Typography>
                <Typography variant="caption">
                  {maintenanceTask?.finishedAt
                    ? moment(maintenanceTask?.finishedAt).format('L LTS')
                    : '-'}
                </Typography>
              </Box>
            </Box>
            <DetailRow
              header={t('common:comment')}
              data={
                maintenanceTask?.finishedComment
                  ? maintenanceTask?.finishedComment
                  : '-'
              }
            />
          </Box>
          <Box>
            <Box
              sx={(theme) => ({
                mt: 1,
                mb: '4px',
                px: 1,
                py: '2px',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                backgroundColor: 'info.main',
                // backgroundColor:
                //   theme.palette.mode === 'light'
                //     ? 'primary.light'
                //     : 'primary.dark',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              })}
            >
              {t('acknowledged')}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  textAlign: 'right',
                }}
              >
                <Typography variant="caption">
                  {maintenanceTask?.acknowledgedByUser?.username
                    ? maintenanceTask?.acknowledgedByUser?.username
                    : '-'}
                </Typography>
                <Typography variant="caption">
                  {maintenanceTask?.acknowledgedAt
                    ? moment(maintenanceTask?.acknowledgedAt).format('L LTS')
                    : '-'}
                </Typography>
              </Box>
            </Box>
            <DetailRow
              header={t('common:comment')}
              data={
                maintenanceTask?.acknowledgedComment
                  ? maintenanceTask?.acknowledgedComment
                  : '-'
              }
            />
          </Box>
          <Box>
            <Box
              sx={(theme) => ({
                mt: 2,
                mb: '4px',
                px: 1,
                py: 1,
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                // backgroundColor:
                //   theme.palette.mode === 'light'
                //     ? 'primary.dark'
                //     : 'primary.main',
                backgroundColor: 'action.disabledBackground',
                color: 'text.primary',
                textAlign: 'left',
              })}
            >
              {t('common:status')}
            </Box>
            <Typography variant="subtitle2">
              {t(maintenanceTask?.status)}
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            {maintenanceTask?.status === 'waiting_for_start' &&
            user?.roles.some(
              (role) => role === 'admin' || role === 'maintenance'
            ) ? (
              <Button
                color="primary"
                variant="contained"
                sx={{
                  height: '90px',
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.main',
                  ':hover': {
                    color: 'primary.contrastText',
                    backgroundColor: 'primary.dark',
                  },
                }}
                fullWidth
                onClick={() => {
                  setIsOpenDialogStartMaintenanceTask(true);
                }}
                disabled={loading}
                startIcon={
                  !loading ? null : (
                    <CircularProgress color="inherit" size={20} />
                  )
                }
              >
                {t('start')}
              </Button>
            ) : null}
            {(maintenanceTask?.status === 'waiting_for_finish' ||
              maintenanceTask?.status === 'rejected') &&
            user?.roles.some(
              (role) => role === 'admin' || role === 'maintenance'
            ) ? (
              maintenanceTask?.startedByUser?.id === user?.id ? (
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    height: '90px',
                    color: 'primary.contrastText',
                    backgroundColor: 'primary.main',
                    ':hover': {
                      color: 'primary.contrastText',
                      backgroundColor: 'primary.dark',
                    },
                  }}
                  fullWidth
                  onClick={() => {
                    setIsOpenDialogFinishMaintenanceTask(true);
                  }}
                  disabled={loading}
                  startIcon={
                    !loading ? null : (
                      <CircularProgress color="inherit" size={20} />
                    )
                  }
                >
                  {t('finish')}
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    height: '90px',
                    color: 'primary.contrastText',
                    backgroundColor: 'warning.main',
                    ':hover': {
                      color: 'primary.contrastText',
                      backgroundColor: 'warning.dark',
                    },
                  }}
                  fullWidth
                  onClick={() => {
                    setIsOpenDialogTakeOverMaintenanceTask(true);
                  }}
                  disabled={loading}
                  startIcon={
                    !loading ? null : (
                      <CircularProgress color="inherit" size={20} />
                    )
                  }
                >
                  {t('take_over')}
                </Button>
              )
            ) : null}
            {maintenanceTask?.status === 'waiting_for_acknowledgement' &&
            user?.roles.some(
              (role) => role === 'admin' || role === 'production'
            ) ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 6 },
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    height: '90px',
                    color: 'primary.contrastText',
                    backgroundColor: 'success.main',
                    ':hover': {
                      color: 'primary.contrastText',
                      backgroundColor: 'success.dark',
                    },
                  }}
                  fullWidth
                  onClick={() => {
                    setIsOpenDialogAcceptMaintenanceTask(true);
                  }}
                  disabled={loading}
                  startIcon={
                    !loading ? null : (
                      <CircularProgress color="inherit" size={20} />
                    )
                  }
                >
                  {t('accept')}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    height: '90px',
                    color: 'primary.contrastText',
                    backgroundColor: 'error.main',
                    ':hover': {
                      color: 'primary.contrastText',
                      backgroundColor: 'error.dark',
                    },
                  }}
                  fullWidth
                  onClick={() => {
                    setIsOpenDialogRejectMaintenanceTask(true);
                  }}
                  disabled={loading}
                  startIcon={
                    !loading ? null : (
                      <CircularProgress color="inherit" size={20} />
                    )
                  }
                >
                  {t('reject')}
                </Button>
              </Box>
            ) : null}
          </Box>
          {errors.length > 0 && (
            <Box mt={5}>
              {errors
                .slice()
                .reverse()
                .map((error) => (
                  <Box key={error} mt={1}>
                    <Alert variant="outlined" severity="error">
                      {t(error)}
                    </Alert>
                  </Box>
                ))}
            </Box>
          )}
        </Paper>
      ) : null}
      <DialogStartMaintenanceTask
        open={isOpenDialogStartMaintenanceTask}
        onClose={() => {
          setIsOpenDialogStartMaintenanceTask(false);
        }}
        onValidate={() => {
          setIsOpenDialogStartMaintenanceTask(false);
          getData();
        }}
        maintenanceTask={maintenanceTask}
      />
      <DialogFinishMaintenanceTask
        open={isOpenDialogFinishMaintenanceTask}
        onClose={() => {
          setIsOpenDialogFinishMaintenanceTask(false);
        }}
        onValidate={() => {
          setIsOpenDialogFinishMaintenanceTask(false);
          getData();
        }}
        maintenanceTask={maintenanceTask}
      />
      <DialogAcceptMaintenanceTask
        open={isOpenDialogAcceptMaintenanceTask}
        onClose={() => {
          setIsOpenDialogAcceptMaintenanceTask(false);
        }}
        onValidate={() => {
          setIsOpenDialogAcceptMaintenanceTask(false);
          getData();
        }}
        maintenanceTask={maintenanceTask}
      />
      <DialogRejectMaintenanceTask
        open={isOpenDialogRejectMaintenanceTask}
        onClose={() => {
          setIsOpenDialogRejectMaintenanceTask(false);
        }}
        onValidate={() => {
          setIsOpenDialogRejectMaintenanceTask(false);
          getData();
        }}
        maintenanceTask={maintenanceTask}
      />
      <DialogTakeOverMaintenanceTask
        open={isOpenDialogTakeOverMaintenanceTask}
        onClose={() => {
          setIsOpenDialogTakeOverMaintenanceTask(false);
        }}
        onValidate={() => {
          setIsOpenDialogTakeOverMaintenanceTask(false);
          getData();
        }}
        maintenanceTask={maintenanceTask}
      />
    </Container>
  );
}

export default MaintenanceTaskDetails;
